/**
 * Web component for Anear autofocus behavior
 *
 * Usage:
 *   <anear-autofocus behavior="end">
 *     <textarea name="text"></textarea>
 *   </anear-autofocus>
 *
 * Or as an attribute on existing elements (for backward compatibility):
 *   <textarea data-anear-autofocus="end"></textarea>
 *
 * The component handles focusing input/textarea elements and optionally
 * setting the cursor to the end of the text.
 */
class AnearAutofocus extends HTMLElement {
  constructor() {
    super()
    this._behavior = null
  }

  static get observedAttributes() {
    return ['behavior']
  }

  connectedCallback() {
    // Read the behavior attribute if it exists
    const behaviorAttr = this.getAttribute('behavior')
    if (behaviorAttr !== null && this._behavior === null) {
      this._behavior = behaviorAttr
    }
    this.applyAutofocus()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'behavior') {
      this._behavior = newValue
      this.applyAutofocus()
    }
  }

  get behavior() {
    return this.getAttribute('behavior') || this._behavior
  }

  set behavior(value) {
    if (value) {
      this.setAttribute('behavior', value)
      this._behavior = value
    } else {
      this.removeAttribute('behavior')
      this._behavior = null
    }
    this.applyAutofocus()
  }

  /**
   * Apply autofocus to the first input/textarea element within this component
   */
  applyAutofocus() {
    // Find the first input or textarea element within this component
    const focusableEl = this.querySelector('input, textarea')
    if (!focusableEl || typeof focusableEl.focus !== 'function') {
      return
    }

    try {
      focusableEl.focus()

      // If behavior is "end", set cursor to end of text
      if (this.behavior === 'end') {
        const value = focusableEl.value ?? ''
        if (typeof focusableEl.setSelectionRange === 'function') {
          focusableEl.setSelectionRange(value.length, value.length)
        }
      }
    } catch (_e) {
      // ignore focus errors
    }
  }
}

// Register the custom element
if (!customElements.get('anear-autofocus')) {
  customElements.define('anear-autofocus', AnearAutofocus)
}

export default AnearAutofocus
